import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@hero/web-lib';
import {
  ArrowRightIcon,
  ArrowUpRightIcon,
  ClientIcon,
  ContactIcon,
  DriverIcon,
  InvoiceIcon,
  LoadMainIcon,
  SavedLocationIcon,
  TransactionIcon,
  TruckIcon,
  WalletIcon,
  type IconProps,
} from '@hero/web-icons';
import { openEntityDrawer, type HeroEntityKind } from './openEntityDrawer';

/**
 * Per-kind glyph for the inline chip. Anything not listed here falls
 * back to the top-right arrow so the chip still telegraphs "opens
 * elsewhere".
 */
const INLINE_KIND_ICON: Partial<Record<HeroEntityKind, FC<IconProps>>> = {
  client: ClientIcon,
  contact: ContactIcon,
  order: LoadMainIcon,
  driver: DriverIcon,
  vehicle: TruckIcon,
  saved_location: SavedLocationIcon,
  invoice: InvoiceIcon,
  account: WalletIcon,
  transaction: TransactionIcon,
};

/**
 * Inline pill tone. Restrained white chip at rest (a hairline slate
 * ring keeps it as a "tag", not a button). Hover inverts it into a
 * solid primary-blue fill — the chip looks like it "lights up" to
 * confirm clickability, and the arrow inherits the white text colour
 * so the affordance reads as one piece. Active darkens it for the
 * tactile press.
 */
const INLINE_TONE = cn(
  'bg-white text-slate-700 ring-1 ring-slate-200/80 shadow-[0_1px_0_rgba(15,23,42,0.04)]',
  'hover:bg-primary-600 hover:text-white hover:ring-primary-600 hover:shadow-sm hover:-translate-y-px',
  'active:bg-primary-700 active:ring-primary-700 active:translate-y-0 active:shadow-none',
);

export interface EntityLinkProps {
  kind: HeroEntityKind;
  entityId: string;
  label: string;
  /** Secondary line — phone, status, route summary, etc. */
  subtitle?: ReactNode;
  icon?: ReactNode;
  /** Inline pill inside markdown vs full-width card under the bubble. */
  variant?: 'inline' | 'card';
  /** Renders inline after the label (e.g. role badges). Card variant only. */
  trailing?: ReactNode;
  className?: string;
}

/**
 * Tappable entity reference — opens the host drawer for clients,
 * orders, drivers, vehicles, and saved locations.
 */
export const EntityLink = ({
  kind,
  entityId,
  label,
  subtitle,
  icon,
  trailing,
  variant = 'card',
  className,
}: EntityLinkProps) => {
  const { t } = useTranslation('m-agent');

  const handleClick = () => openEntityDrawer(kind, entityId);

  if (variant === 'inline') {
    const KindIcon = INLINE_KIND_ICON[kind];
    return (
      <button
        type='button'
        onClick={handleClick}
        aria-label={t('entity_link.open', { label, defaultValue: 'Open {{label}}' })}
        title={label}
        className={cn(
          'group/entitylink inline-flex items-center gap-1 align-baseline',
          'rounded-md px-2 py-0.5 -my-0.5 text-[0.95em] font-medium',
          'transition-all duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-1',
          INLINE_TONE,
          className,
        )}
      >
        {KindIcon ? (
          <KindIcon className='size-3.5 flex-none opacity-70 transition-opacity duration-150 group-hover/entitylink:opacity-100' />
        ) : null}
        <span className='leading-none [font-variant-numeric:tabular-nums]'>{label}</span>
        {KindIcon ? null : (
          <ArrowUpRightIcon
            className={cn(
              'size-3 flex-none opacity-60 transition-transform duration-150',
              'group-hover/entitylink:opacity-100',
              'group-hover/entitylink:translate-x-px',
              'group-hover/entitylink:-translate-y-px',
            )}
          />
        )}
      </button>
    );
  }

  return (
    <button
      type='button'
      onClick={handleClick}
      aria-label={t('entity_link.open', { label, defaultValue: 'Open {{label}}' })}
      className={cn(
        'group flex w-full items-center gap-3 rounded-2xl border border-slate-200/90',
        'bg-white px-3.5 py-3 text-start shadow-sm transition-all',
        'hover:border-primary-200 hover:bg-primary-50/30 hover:shadow-md',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400',
        className,
      )}
    >
      {icon && (
        <span className='flex size-9 flex-none items-center justify-center rounded-xl bg-primary-50 text-primary-600 transition-colors group-hover:bg-primary-100'>
          {icon}
        </span>
      )}
      <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
        <span className='flex min-w-0 items-center gap-2'>
          <span className='truncate text-sm font-semibold text-slate-900'>{label}</span>
          {trailing ? <span className='shrink-0'>{trailing}</span> : null}
        </span>
        {subtitle ? (
          <span className='truncate text-xs text-slate-500'>{subtitle}</span>
        ) : null}
      </span>
      <ArrowRightIcon className='size-4 flex-none text-slate-400 transition-transform group-hover:translate-x-0.5 group-hover:text-primary-600 rtl:rotate-180 rtl:group-hover:-translate-x-0.5' />
    </button>
  );
};
